/**
 * evidenceTagTargets.js
 * ---------------------
 * What a tag made on one triage-proforma row points at.
 *
 * The proforma shows answers by Category of Information, and a category can be
 * one question or several summarised into one line. That decides what a tag
 * can name:
 *
 *   - A row backed by a single question tags that question, so the tag lands
 *     on the same answer the decision wizard's evidence panel lists and the
 *     two surfaces show each other's tags.
 *   - A consolidated row has no single question behind it, so it is tagged as
 *     the category it displays and carries the summary lines the clinician
 *     actually read.
 *
 * A tag also records WHICH line it points at, so the answer's parts have to be
 * split the same way the clinician sees them.
 */

import { formatAnswerItems } from './otherFactorsSurfacing';

/** Split displayed text into the lines a tag can point at individually. */
export function displayedLines(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.replace(/^[\u2022\-\s]+/, '').trim())
    .filter(Boolean);
}

export function tagTargetFor({ formKey, role, coi, question, answers, displayValue }) {
  if (question) {
    // The structured items where the answer has them, so a tag made here
    // matches the one the wizard's evidence panel would make on the same
    // answer. Where it does not (a composed or derived display value), the
    // displayed text stands in rather than the row losing its tag button.
    const values = formatAnswerItems(question, answers?.[question.questionId]);
    const lines = values.length ? values : displayedLines(displayValue);
    if (!lines.length) return null;
    return {
      formKey,
      role,
      questionId: question.questionId,
      label: coi || question.label || question.questionId,
      values: lines,
    };
  }
  const lines = displayedLines(displayValue);
  if (!lines.length) return null;
  return {
    formKey,
    role,
    // Namespaced so a category can never collide with a questionId.
    questionId: `category:${coi}`,
    label: coi,
    values: lines,
  };
}

